/*
 * dashcam/gsensor.js — "was that a crash?" from DeviceMotionEvent samples.
 *
 * The complaint every phone dash-cam app collects is the same: the G-sensor
 * locks a file on every pothole and speed bump, the event folder fills with
 * nothing, and people switch the sensor off. A hit from a pothole and a hit
 * from a car are told apart by DIRECTION, not by size: a wheel dropping into a
 * hole shakes the car up and down, a collision or an emergency stop pushes it
 * along the road or across it. So by default the detector measures only the
 * acceleration in the road's plane — the part perpendicular to gravity — and a
 * vertical jolt of any size does not count. Switched off, it is the plain
 * magnitude the hardware dash cams use.
 *
 * What it needs per sample (m/s², as DeviceMotionEvent gives them):
 *   lin  — `acceleration` (gravity already removed by the phone's sensor
 *          fusion), may be null (some Android builds, all desktop sensors);
 *   incl — `accelerationIncludingGravity`, may be null.
 * Gravity's direction: incl − lin when both exist (exact), else a slow
 * low-pass of incl; when only lin exists the plane is unknown and the full
 * magnitude is used, whatever the setting.
 *
 * A trigger is the mean over `windowMs` crossing the threshold — one bad
 * sample from a rattling mount is not a crash, the 50–150 ms of a real impact
 * is. After a trigger the detector stays quiet for `refractoryMs`: the
 * recorder already extends a running event, so a second trigger from the
 * same crash is noise.
 *
 * Guards: tests/js/dashcam-gsensor.test.mjs.
 */

export const G = 9.80665;

function len(v) { return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z); }
function num(v) { return v && isFinite(v.x) && isFinite(v.y) && isFinite(v.z) ? v : null; }
function vec(v) { return v ? { x: +v.x || 0, y: +v.y || 0, z: +v.z || 0 } : null; }

export class ImpactDetector {
    constructor(opts = {}) {
        this.threshold = opts.thresholdG != null ? opts.thresholdG : 1.5;
        this.horizontal = opts.horizontalOnly !== false;
        this.windowMs = opts.windowMs != null ? opts.windowMs : 50;
        this.refractoryMs = opts.refractoryMs != null ? opts.refractoryMs : 2500;
        this.gravityTauMs = opts.gravityTauMs != null ? opts.gravityTauMs : 800;
        this.reset();
    }

    reset() {
        this.grav = null;
        this.lastT = null;
        this.buf = [];
        this.lastTrigger = -Infinity;
        this.peakBuf = [];
        this.samples = 0;
        this.haveLinear = false;
    }

    setThreshold(g) { if (isFinite(g) && g > 0) this.threshold = g; }
    setHorizontal(on) { this.horizontal = !!on; }

    /**
     * One DeviceMotionEvent's worth. s: {t (ms), lin, incl}
     * → {g, raw, trigger, planeKnown} — g is the windowed value compared with
     * the threshold, raw the instantaneous one.
     */
    push(s) {
        const t = s.t;
        const lin = num(vec(s.lin)), incl = num(vec(s.incl));
        if (!lin && !incl) return { g: 0, raw: 0, trigger: false, planeKnown: false };
        this.samples++;
        if (lin) this.haveLinear = true;

        // gravity estimate
        let gNow = null;
        if (lin && incl) gNow = { x: incl.x - lin.x, y: incl.y - lin.y, z: incl.z - lin.z };
        else if (incl) gNow = incl;
        if (gNow) {
            if (!this.grav || this.lastT == null) this.grav = { ...gNow };
            else {
                const dt = Math.max(0, t - this.lastT);
                // with fused gravity (both vectors) a short constant is enough; from
                // raw incl it must be slow, or the impact itself would bend "down"
                const tau = lin ? 150 : this.gravityTauMs;
                const k = 1 - Math.exp(-dt / tau);
                this.grav.x += (gNow.x - this.grav.x) * k;
                this.grav.y += (gNow.y - this.grav.y) * k;
                this.grav.z += (gNow.z - this.grav.z) * k;
            }
        }
        this.lastT = t;

        // linear acceleration
        let a = lin;
        if (!a) {
            // before the low-pass has settled, incl − grav is ~0: no false alarm on the first sample
            a = { x: incl.x - this.grav.x, y: incl.y - this.grav.y, z: incl.z - this.grav.z };
        }

        let planeKnown = false;
        let mag;
        const gl = this.grav ? len(this.grav) : 0;
        if (this.horizontal && gl > 3) {
            planeKnown = true;
            const ux = this.grav.x / gl, uy = this.grav.y / gl, uz = this.grav.z / gl;
            const along = a.x * ux + a.y * uy + a.z * uz;
            const hx = a.x - along * ux, hy = a.y - along * uy, hz = a.z - along * uz;
            mag = Math.sqrt(hx * hx + hy * hy + hz * hz);
        } else {
            mag = len(a);
        }
        const raw = mag / G;

        // time window mean
        this.buf.push({ t, v: raw });
        while (this.buf.length && this.buf[0].t < t - this.windowMs) this.buf.shift();
        let sum = 0;
        for (const b of this.buf) sum += b.v;
        const g = sum / this.buf.length;

        // for the live meter: peak over the last 10 s
        this.peakBuf.push({ t, v: g });
        while (this.peakBuf.length && this.peakBuf[0].t < t - 10000) this.peakBuf.shift();

        let trigger = false;
        if (g >= this.threshold && t - this.lastTrigger >= this.refractoryMs) {
            trigger = true;
            this.lastTrigger = t;
        }
        return { g, raw, trigger, planeKnown };
    }

    /** Highest windowed value in the last 10 s — what the settings meter shows. */
    peak() {
        let m = 0;
        for (const b of this.peakBuf) if (b.v > m) m = b.v;
        return m;
    }
}

/** DeviceMotionEvent → the detector's sample shape. */
export function sampleFromEvent(e, t) {
    const a = e && e.acceleration, i = e && e.accelerationIncludingGravity;
    const ok = (v) => v && v.x != null && v.y != null && v.z != null;
    return { t, lin: ok(a) ? a : null, incl: ok(i) ? i : null };
}

/** Threshold presets, g. The middle one is the default. */
export const PRESETS = Object.freeze({ sensitive: 1.0, normal: 1.5, rough: 2.5 });
